import { InteractionsIconMapMedium } from "@/common/helpers";
import {
	MenuItem,
	MenuList,
	OnOpenChangeData,
	OpenPopoverEvents,
	Text,
} from "@fluentui/react-components";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useStyle } from "./style";
import { PopoverView } from "@/components/atoms/popover";
import { WorkloadMenuProps, WorkloadMenuType } from "./types";
import { agentWorkloadQueryKey, getAgentWorkload } from "@/repository/queryFns";
import { AgentWorkload, WorkloadInteractionGroup } from "@/repository/models";
import { InteractionType } from "@/common/types";
import { useQuery } from "@tanstack/react-query";
import { useResponsiveBreakpoints } from "@/hooks/responsive";

export const WorkloadMenu = (props: WorkloadMenuProps) => {
	const intl = useIntl();
	const classes = useStyle();

	const [isQueryEnabled, setIsQueryEnabled] = useState<boolean>(false);
	const [workloadMenuItems, setWorkloadMenuItems] = useState<JSX.Element[]>([]);

	const [popoverOpen, setPopoverOpen] = useState<boolean>(false);

	const { isMobile } = useResponsiveBreakpoints();

	const interactionName = useCallback(
		(type: InteractionType, plural: boolean = false) =>
			intl.formatMessage({
				id: `Interaction.${InteractionType[type]}${plural ? "s" : ""}`,
			}),
		[intl]
	);

	const workloadInteractionGroups = useCallback(
		(items: WorkloadInteractionGroup[]) => {
			return items.map((item: WorkloadInteractionGroup) => {
				return (
					<MenuItem
						icon={InteractionsIconMapMedium[item.type]}
						secondaryContent={<Text>{item.groupUtilization}%</Text>}
						className={classes.menuItem}
						key={item.type}
					>
						{item.count} {interactionName(item.type, item.count > 1)}
					</MenuItem>
				);
			});
		},
		[]
	);

	const onOpenChange = (_e: OpenPopoverEvents, data: OnOpenChangeData) => {
		if (!data.open || !props.totalWorkload) {
			setPopoverOpen(false);
			return;
		}

		setIsQueryEnabled(true);
		setPopoverOpen(true);
	};

	const { data: agentWorkload } = useQuery<AgentWorkload>({
		queryKey: [agentWorkloadQueryKey, props.agentId],
		queryFn: () => getAgentWorkload(props.agentId),
		enabled: isQueryEnabled,
	});

	useEffect(() => {
		if (agentWorkload) {
			if (agentWorkload.totalWorkload == 0) {
				setPopoverOpen(false);
			}
			setWorkloadMenuItems(
				workloadInteractionGroups(
					agentWorkload?.workloadInteractionGroups ?? []
				)
			);
		} else {
			setPopoverOpen(false);
		}
	}, [agentWorkload]);

	return (
		<PopoverView
			trigger={props.trigger}
			content={<MenuList>{workloadMenuItems}</MenuList>}
			positioning={{
				position:
					props.menuType == WorkloadMenuType.CurrentAgent && !isMobile
						? "before"
						: "below",
			}}
			classes={{ content: classes.popoverContent }}
			onOpenChange={onOpenChange}
			open={popoverOpen}
			openOnHover={props.openOnHover}
		></PopoverView>
	);
};
